import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import {
  Box,
  CircularProgress,
  Switch,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Button,
} from "@mui/material";
import {
  Add,
  GridViewOutlined,
  ViewTimelineOutlined,
} from "@mui/icons-material";
import { useFetchTask } from "../hooks/useFetchTask";
import { DrawerControl, SearchField } from "../../../shared/components";

import {
  TaskDetails,
  RaiseTask,
  TableView,
  AllTasks,
  ContractorObjectDrawer,
} from "../components/index";
import { useCapabilities } from "../../../hooks/useCapabilities";
import { useTaskFilter } from "../hooks/useTaskFilter";

type TaskObjectType =
  | "task"
  | "recurring_task"
  | "stage_gate"
  | "checklist"
  | "activity"
  | "risk"
  | "queries"
  | "meeting"
  | "material_request"
  | "resource_request";

export const Task: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAllowedToAddTask, isContractorPerson } = useCapabilities();

  const selected = searchParams.get("selected");
  const isOpen = searchParams.get("isOpen") === "true";
  const taskId = searchParams.get("taskId");
  const isEdit = searchParams.get("isEdit");
  const objectType = searchParams.get("objectType");
  const objectId = searchParams.get("objectId");
  const project_id = searchParams.get("projectId");

  const shouldOpenContractorDrawer = isOpen && !!objectType && !!objectId;

  const taskType: Record<string, string> = {
    risk: "Risk",
    queries: "Query",
    meeting: "Meeting",
    recurring_task: "Recurring Task",
    checklist: "Checklist",
    activity: "Activity",
    stage_gate: "Stage Gate",
    task: "Task",
  };

  const [alignment, setAlignment] = useState<string | null>("center");
  const [raiseTask, setRaiseTask] = useState<boolean>(false);

  const {
    priorityFilter,
    setPriorityFilter,
    actionTypeFilter,
    setActionTypeFilter,
    statusFilter,
    setStatusFilter,
    ownerFilter,
    setOwnerFilter,
    assignedToMe,
    setAssignedToMe,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
  } = useTaskFilter(projectId);

  const onHandleEdit = (id?: string | any) => {
    const params = new URLSearchParams(searchParams);
    params.set("isEdit", "true");

    let currentTaskId = taskId;
    if (typeof id === "string" || typeof id === "number") {
      currentTaskId = String(id);
      params.set("taskId", currentTaskId);
    }

    if (currentTaskId && taskData) {
      const task = taskData.find((t: any) => String(t.task_id) === String(currentTaskId));
      if (task) {
        if (task.object_type) params.set("objectType", task.object_type);
        if (task.object_id) params.set("objectId", String(task.object_id));
      }
    }

    setSearchParams(params, { replace: true });
  };

  const contractorDrawerRefetchFunctions = React.useRef<Record<string, () => void>>({});

    const handleRefetchRequired = React.useCallback(
    (drawerName: string, refetchFn: () => void) => {
      contractorDrawerRefetchFunctions.current[drawerName] = refetchFn;
    },
    []
  );

  const handleCloseEdit = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("isEdit");
    if (!isOpen) {
      params.delete("objectType");
      params.delete("objectId");
    }
    setSearchParams(params, { replace: true });

    // Refetch data from contractor drawers if they have refetch functions
    Object.values(contractorDrawerRefetchFunctions).forEach((refetchFn) => {
      refetchFn?.();
    });
  };

  const handleAlignment = (
    _e: React.MouseEvent<HTMLElement>,
    newAlignment: string | null,
  ) => {
    setAlignment(newAlignment);
  };

    const {
        isPending,
        isRefetching,
        taskData,
        pagination,
        searchDraft,
        handleChangeSearch,
        handleClear,
        handleSearch,
        changePage,
        changeRowsPerPage,
        refetch,
        switchStatus
    } = useFetchTask({
        projectId,
        object_type: actionTypeFilter.length > 0 ? actionTypeFilter : null,
        object_id: null,
        my_tasks: assignedToMe,
        priorityFilter: priorityFilter.length > 0 ? priorityFilter : undefined,
        statusFilter: statusFilter.length > 0 ? statusFilter : undefined,
        ownerFilter: ownerFilter.length > 0 ? ownerFilter : undefined,
        initialPage: page,
        initialLimit: rowsPerPage,
    });

  const handleAssignedToMeChange = () => {
    setAssignedToMe(!assignedToMe);
    handlePageChange(1);
  };

  const handleStatusChange = async (taskId: string, newStatus: string) => {
    await switchStatus(taskId, newStatus);
    refetch();
  };

  const openDetailsDrawer = (taskId: number | null) => {
    const params = new URLSearchParams(searchParams);
    params.set("selected", "true");
    params.set("taskId", String(taskId));
    setSearchParams(params, { replace: true });
  };

  const closeDetailsDrawer = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("selected");
    params.delete("taskId");
    setSearchParams(params, { replace: true });
  };

  const openContractorDrawer = (
    objectType: string,
    objectId: number,
    projectId?: number | null,
    taskId?: number | null,
  ) => {
    const params = new URLSearchParams();
    params.set("isOpen", "true");
    params.set("objectType", objectType);
    params.set("objectId", String(objectId));
    if (projectId) {
      params.set("projectId", String(projectId));
    }
    if (taskId) {
      params.set("taskId", String(taskId));
    }
    setSearchParams(params, { replace: true });
  };

  const closeContractorDrawer = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("isOpen");
    params.delete("objectType");
    params.delete("objectId");
    params.delete("taskId");
    params.delete("projectId");
    // Also remove any legacy params
    params.delete("type");
    params.delete("id");
    setSearchParams(params, { replace: true });
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    changePage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    changeRowsPerPage(newRowsPerPage);
  };

  const handleCompleteTask = async (taskId: string) => {
    // This will be implemented to call the Action Items complete functionality
  };

  if (isPending) {
    return (
      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
        gap={2}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box
        sx={{
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
          gap: 2,
          p: 2,
        }}
      >
        <ToggleButtonGroup
          value={alignment}
          exclusive
          onChange={handleAlignment}
          aria-label="view toggle"
          size="small"
        >
          <ToggleButton value="center" aria-label="list view">
            <ViewTimelineOutlined sx={{ color: "text.secondary" }} />
          </ToggleButton>
          <ToggleButton value="left" aria-label="grid view">
            <GridViewOutlined sx={{ color: "text.secondary" }} />
          </ToggleButton>
        </ToggleButtonGroup>

        <SearchField
          searchTerm={searchDraft}
          handleSearch={handleChangeSearch}
          handleClearSearch={() => {
            handleClear();
            handlePageChange(1);
          }}
          handleApplySearch={() => {
            handleSearch();
            handlePageChange(1);
          }}
        />

        {!isContractorPerson && (
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Typography variant="subtitle2">Assigned to Me:</Typography>
            <Switch
              checked={assignedToMe}
              onChange={handleAssignedToMeChange}
            />
          </Box>
        )}

        {isAllowedToAddTask && (
          <Button
            startIcon={<Add />}
            onClick={() => setRaiseTask(true)}
            sx={{ marginLeft: "auto" }}
          >
            RAISE NEW TASK
          </Button>
        )}
      </Box>

            {isRefetching ? (
                <Box
                    height="100%"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    width="100%"
                    gap={2}
                >
                    <CircularProgress />
                </Box>
            ) : alignment === "center" ? (
                <TableView
                    taskData={taskData}
                    pagination={pagination}
                changeRowsPerPage={handleRowsPerPageChange}
                changePage={handlePageChange}
                    openDrawer={openDetailsDrawer}
                    statusFilter={statusFilter}
                    priorityFilter={priorityFilter}
                    actionTypeFilter={actionTypeFilter}
                    ownerFilter={ownerFilter}
                    onStatusFilterChange={(keys: string[]) => {
                      setStatusFilter(keys);
                      handlePageChange(1);
                    }}
                    onPriorityFilterChange={(keys: string[]) => {
                      setPriorityFilter(keys);
                      handlePageChange(1);
                    }}
                    onActionTypeFilterChange={(keys: string[]) => {
                      setActionTypeFilter(keys);
                      handlePageChange(1);
                    }}
                    onOwnerFilterChange={(keys: string[]) => {
                      setOwnerFilter(keys);
                      handlePageChange(1);
                    }}
                    projectId={projectId}
                    isContractorPerson={isContractorPerson}
                    onOpenContractorDrawer={openContractorDrawer}
                />
            ) : (
                <AllTasks
                    taskData={taskData}
                    handleStatusChange={handleStatusChange}
                    onEdit={onHandleEdit}
                    openDetailsDrawer={openDetailsDrawer}
                    pagination={pagination}
                changeRowsPerPage={handleRowsPerPageChange}
                changePage={handlePageChange}
                />
            )}

      <DrawerControl
        title={"Foundation Design Review"}
        subTitle={""}
        isOpen={!!selected}
        handleClose={closeDetailsDrawer}
        children={
          taskId && (
            <TaskDetails
              taskId={taskId}
              onEdit={onHandleEdit}
              onClose={() => closeDetailsDrawer()}
            />
          )
        }
      />

      <DrawerControl
        title={"Create/Edit Task"}
        subTitle={""}
        isOpen={!!isEdit}
        handleClose={() => {
          handleCloseEdit();
        }}
        children={
          <RaiseTask
            isEdit={true}
            taskId={taskId}
            object_type={objectType as TaskObjectType}
            object_id={objectId}
            onClose={() => {
              handleCloseEdit();
            }}
            refetch={refetch}
            enabledFields={["task_category"]}
            projectId={projectId}
          />
        }
      />

      <DrawerControl
        title={"Create/Edit Task"}
        subTitle={""}
        isOpen={raiseTask}
        handleClose={() => {
          setRaiseTask(false);
        }}
        children={
          <RaiseTask
            isEdit={false}
            taskId={null}
            onClose={() => {
              setRaiseTask(false);
            }}
            refetch={() => refetch()}
            enabledFields={["task_category"]}
            projectId={projectId}
          />
        }
      />

      <DrawerControl
        title={`${objectType ? taskType[objectType] : objectType} Details`}
        subTitle={""}
        isOpen={shouldOpenContractorDrawer}
        handleClose={closeContractorDrawer}
        children={
          objectType && objectId ? (
            <ContractorObjectDrawer
              objectType={objectType}
              objectId={Number(objectId)}
              projectId={project_id ? String(project_id) : undefined}
              taskId={taskId ? Number(taskId) : undefined}
              onCompleteTask={handleCompleteTask}
              isAssignedToUser={assignedToMe}
              refetch={refetch}
              onRefetchRequired={handleRefetchRequired}
              handleClose={closeContractorDrawer}
            />
          ) : null
        }
      />
    </Box>

  );
};
